// Web Audio API 合成音效模块
// 首次用户手势时 init()，之后 playSfx() 触发合成音

export type SfxName =
  | "play"
  | "damage"
  | "dodge"
  | "void"
  | "heal"
  | "death"
  | "turn"
  | "reflect"
  | "judgment"
  | "judgment-success";

let ctx: AudioContext | null = null;
let master: GainNode | null = null;

export function init(): void {
  if (ctx) return;
  try {
    ctx = new (window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext)();
    master = ctx.createGain();
    master.gain.value = 0.18;
    master.connect(ctx.destination);
  } catch {
    ctx = null;
  }
}

// 通用：oscillator → gain → master，带指数衰减包络
function tone(
  type: OscillatorType,
  freq: number,
  dur: number,
  vol: number = 1,
  sweepTo?: number
): void {
  if (!ctx || !master) return;
  const t0 = ctx.currentTime;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (sweepTo != null) {
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(1, sweepTo),
      t0 + dur
    );
  }
  g.gain.setValueAtTime(vol, t0);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  osc.connect(g);
  g.connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

function noise(dur: number, vol: number = 0.5): void {
  if (!ctx || !master) return;
  const t0 = ctx.currentTime;
  const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 800;
  const g = ctx.createGain();
  g.gain.setValueAtTime(vol, t0);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
  src.connect(filter);
  filter.connect(g);
  g.connect(master);
  src.start(t0);
  src.stop(t0 + dur);
}

export function playSfx(name: SfxName): void {
  if (!ctx || !master) return;
  switch (name) {
    case "play":
      tone("square", 660, 0.08, 0.5);
      break;
    case "damage":
      tone("sawtooth", 110, 0.18, 0.7);
      noise(0.12, 0.3);
      break;
    case "dodge":
      tone("sine", 800, 0.2, 0.5, 200);
      break;
    case "void":
      tone("sine", 400, 0.3, 0.4, 100);
      break;
    case "heal":
      tone("triangle", 880, 0.25, 0.5);
      setTimeout(() => tone("triangle", 1320, 0.2, 0.4), 80);
      break;
    case "death":
      tone("sawtooth", 55, 0.6, 0.6);
      break;
    case "turn":
      tone("sine", 523, 0.4, 0.4);
      setTimeout(() => tone("sine", 784, 0.3, 0.3), 100);
      break;
    case "reflect":
      tone("sine", 300, 0.15, 0.5, 900);
      break;
    case "judgment":
      // 神秘上行音阶：营造翻牌判定感
      tone("triangle", 440, 0.15, 0.4);
      setTimeout(() => tone("triangle", 554, 0.15, 0.35), 80);
      setTimeout(() => tone("triangle", 659, 0.2, 0.3), 160);
      break;
    case "judgment-success":
      // 判定成功：明亮和弦上行
      tone("triangle", 523, 0.12, 0.4);
      setTimeout(() => tone("triangle", 659, 0.12, 0.35), 60);
      setTimeout(() => tone("triangle", 784, 0.25, 0.3), 120);
      break;
  }
}
